import { Image } from 'expo-image';
import { Href, usePathname } from 'expo-router';
import { Link } from 'expo-router';
import { StyleSheet, View } from 'react-native';

import { urnBlack } from '@/assets/images';
import { donateIcon, engageIcon, homeIcon, impactIcon, profileIcon, transactionsIcon } from '@/assets/images/icons';
import { Button } from '@/components/ui/button';
import { SheetClose, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/authContext';
import { i18n } from '@/utils/i18n';

import { UserType } from './settings/OptionItem';
import Text from './Text';

const DrawerButton = ({
  iconSource,
  name,
  title,
  className
}: {
  iconSource: string;
  name: Href;
  title: string;
  className?: string;
}) => {
  const pathname = usePathname();
  const isSelected = pathname.includes(name.toString());

  return (
    <Link href={name}>
      <SheetClose asChild>
        <Button
          className={['gap-x-2 w-full justify-start', className].filter(Boolean).join(' ')}
          variant={isSelected ? 'outline' : 'link'}>
          <Image
            style={styles.navBarIcon}
            source={iconSource}
            contentFit="contain"
            tintColor={isSelected ? '#E82222' : 'black'}
          />
          <Text style={{ color: isSelected ? '#E82222' : 'black' }}>{title}</Text>
        </Button>
      </SheetClose>
    </Link>
  );
};

const DrawerView = () => {
  const { state } = useAuth();

  const isNonProfitOrg = state.user?.user.role === 'NON_PROFIT_ORG';

  const routesOptions: { name: string; title: string; iconSource: string; userType: UserType[] }[] = [
    {
      name: '/home/npoTabs',
      title: i18n.t('bottom_tab.home'),
      iconSource: homeIcon,
      userType: ['ALL']
    },
    {
      name: '/donation',
      title: i18n.t('bottom_tab.donation'),
      iconSource: donateIcon,
      userType: ['INDIVIDUAL', 'BROWSE_ONLY']
    },
    {
      name: '/engage/volunteerEngage',
      title: i18n.t('bottom_tab.engage'),
      iconSource: engageIcon,
      userType: ['ALL']
    },
    {
      name: '/impact',
      title: i18n.t('bottom_tab.impact'),
      iconSource: impactIcon,
      userType: ['INDIVIDUAL', 'BROWSE_ONLY']
    },
    {
      name: '/transactionsNpo',
      title: i18n.t('bottom_tab.transactions'),
      iconSource: transactionsIcon,
      userType: ['NON_PROFIT_ORG']
    },
    {
      name:
        state.authReady && !state.user
          ? '/profile/browseOnly'
          : isNonProfitOrg
            ? '/profile/npoProfile/insights'
            : '/profile/individual',
      title: i18n.t('bottom_tab.profile'),
      iconSource: profileIcon,
      userType: ['INDIVIDUAL', 'NON_PROFIT_ORG']
    }
  ];

  const menuRoutes = routesOptions.filter(
    (item) =>
      (state.user?.user.role && item.userType.includes(state.user?.user.role)) ||
      item.userType[0] === 'ALL' ||
      (!state.user?.user.role && item.userType.includes('BROWSE_ONLY'))
  );

  return (
    <SheetContent
      side={'left'}
      style={{
        background: 'linear-gradient(white 50%, #B8B8B8 100%)',
        border: 'none'
      }}>
      <SheetHeader>
        <SheetTitle>
          <View style={styles.sheetTitle}>
            <Image cachePolicy={'memory'} source={urnBlack} style={styles.logo} />
            <Text fontWeight="900" style={styles.title}>
              {i18n.t('header.bs')}
            </Text>
          </View>
        </SheetTitle>
        {menuRoutes.map((route) => (
          <DrawerButton key={route.name} iconSource={route.iconSource} name={route.name as Href} title={route.title} />
        ))}
      </SheetHeader>
    </SheetContent>
  );
};

const styles = StyleSheet.create({
  navBarIcon: {
    width: 32,
    height: 32
  },

  logo: {
    height: 20,
    width: 20,
    paddingVertical: 20
  },

  title: {
    flex: 1,
    fontSize: 14,
    color: 'black',
    textTransform: 'uppercase'
  },

  sheetTitle: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center'
  }
});

export default DrawerView;
